import { useMemo, useState } from "react";
import { Text, Group, Stack, Paper, Flex, Divider } from "@mantine/core";
import { IconGripVertical } from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useFormStore } from "@/store/form-builder";
import type { QuestionScope } from "@/store/form-builder";
import { QuestionCard } from "../../QuestionCard/QuestionCard";
import { AddFieldMenu } from "../../Shared/AddFieldMenu";
import { useIsNarrowEditor } from "../../../hooks/useIsNarrowEditor";
import type { FormQuestion } from "../../../types/form.types";
import { getFieldIcon } from "../../../data/fieldTypes";
import { BUILDER_LABELS } from "../../../data/builderLabels";

const COLUMN_WIDTH = 40;

function SortableQuestionItem({
  question,
  scope,
  index,
  total,
  isLastChild,
  hasConditionalChildren,
  depth,
  connectorColumns,
  isDraggedDescendant,
  compact,
}: {
  question: FormQuestion;
  scope: QuestionScope;
  index: number;
  total: number;
  isLastChild?: boolean;
  hasConditionalChildren?: boolean;
  depth: number;
  connectorColumns: boolean[];
  isDraggedDescendant: boolean;
  compact: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging || isDraggedDescendant ? 0 : 1,
        position: "relative",
        paddingBottom: 8,
      }}
    >
      {/* Render vertical connector lines for each depth column */}
      {connectorColumns.map((showLine, colIdx) => {
        // Each column is at colIdx * COLUMN_WIDTH + 11 (center of the 40px column)
        const x = colIdx * COLUMN_WIDTH + 11;
        const isLastColumn = colIdx === connectorColumns.length - 1;

        if (isLastColumn) {
          // This is the current question's own connector column
          // Vertical line from top down to the horizontal branch center,
          // and optionally continuing below if not the last child
          return (
            <div key={colIdx}>
              {/* Line from top to center of card */}
              <div
                style={{
                  position: "absolute",
                  left: x,
                  top: 0,
                  bottom: isLastChild ? "calc(50% + 4px)" : 0,
                  width: 2,
                  background: "#646d7e",
                  zIndex: 1,
                }}
              />
            </div>
          );
        }

        // Ancestor column — draw a full-height continuation line if there are more siblings at that level
        if (!showLine) return null;
        return (
          <div
            key={colIdx}
            style={{
              position: "absolute",
              left: x,
              top: 0,
              bottom: 0,
              width: 2,
              background: "#646d7e",
              zIndex: 1,
            }}
          />
        );
      })}

      {/* Parent's downward line to first child */}
      {hasConditionalChildren && (
        <div
          style={{
            position: "absolute",
            left: depth * COLUMN_WIDTH + 11,
            top: "100%",
            bottom: 0,
            width: 2,
            background: "#646d7e",
            zIndex: 1,
          }}
        />
      )}

      <QuestionCard
        question={question}
        scope={scope}
        index={index}
        total={total}
        dragHandleProps={{ ...attributes, ...listeners }}
        isLastChild={isLastChild}
        hasConditionalChildren={hasConditionalChildren}
        depth={depth}
        noMarginBottom
        compact={compact}
      />
    </div>
  );
}

function QuestionDragOverlay({
  question,
  childQuestions,
  compact,
}: {
  question: FormQuestion;
  childQuestions: FormQuestion[];
  compact: boolean;
}) {
  const Icon = getFieldIcon(question.type);

  if (compact) {
    return (
      <Stack gap={6} style={{ maxWidth: 500 }}>
        <Paper
          radius="8px"
          withBorder
          shadow="lg"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 40,
            padding: "0 10px",
            background: "var(--color-white)",
            border: "2px solid #004ddb",
            opacity: 0.9,
            cursor: "grabbing",
          }}
        >
          <IconGripVertical size={14} color="#afb8c5" />
          <Icon size={14} color="#646d7e" />
          <Text size="sm" fw={500} c="var(--text-default,#363d4d)">
            {question.question || "Untitled field"}
          </Text>
        </Paper>
        {childQuestions.map((child) => {
          const ChildIcon = getFieldIcon(child.type);
          return (
            <Paper
              key={child.id}
              ml={40}
              radius="8px"
              withBorder
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 40,
                padding: "0 10px",
                background: "var(--bg-neutral-medium,#f0f2f5)",
                opacity: 0.85,
              }}
            >
              <ChildIcon size={14} color="#646d7e" />
              <Text size="sm" fw={500} c="var(--text-default,#363d4d)">
                {child.question || "Untitled field"}
              </Text>
            </Paper>
          );
        })}
      </Stack>
    );
  }

  return (
    <Stack gap={0} style={{ maxWidth: 700 }}>
      <Paper
        p="sm"
        radius="lg"
        withBorder
        shadow="lg"
        style={{ border: "2px solid #004ddb", opacity: 0.9, cursor: "grabbing" }}
      >
        <Group gap={8}>
          <IconGripVertical size={16} color="#afb8c5" />
          <Icon size={14} color="#646d7e" />
          <Text size="sm" fw={500} c="dimmed">
            {question.typeName}
          </Text>
          <Text size="sm" fw={600} c="#363d4d" ml={4}>
            {question.question || "Untitled question"}
          </Text>
        </Group>
      </Paper>
      {childQuestions.map((child) => {
        const ChildIcon = getFieldIcon(child.type);
        return (
          <Paper
            key={child.id}
            px="md"
            py={6}
            radius="md"
            ml={40}
            mt={-2}
            style={{
              background: "#f0f2f5",
              border: "1px solid #e2e6eb",
              opacity: 0.85,
            }}
          >
            <Group gap={8}>
              <ChildIcon size={14} color="#646d7e" />
              <Text size="sm" fw={500} c="dimmed">
                {child.typeName}
              </Text>
              <Text size="sm" fw={600} c="#363d4d">
                {child.question || "Untitled question"}
              </Text>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}

interface Props {
  scope: QuestionScope;
  questions: FormQuestion[];
  emptyStateLabel?: string;
}

export function QuestionList({ scope, questions, emptyStateLabel }: Props) {
  const { addQuestion, moveChildGroup, moveQuestionGroup, getConditionalChildIds } = useFormStore();
  const [isDragOver, setIsDragOver] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const compact = !!scope.groupId;

  // Build full tree-aware connector info for multi-level nesting
  const connectorInfo = useMemo(() => {
    const qs = questions;

    // Build parent→children map, sorted by current visual position in qs
    const childrenOf: Record<string, string[]> = {};
    const parentOf: Record<string, string> = {};
    for (const q of qs) {
      const kids = getConditionalChildIds(qs, q.id);
      if (kids.length > 0) {
        const sortedKids = [...kids].sort(
          (a, b) => qs.findIndex((x) => x.id === a) - qs.findIndex((x) => x.id === b),
        );
        childrenOf[q.id] = sortedKids;
        for (const kid of sortedKids) {
          parentOf[kid] = q.id;
        }
      }
    }

    // Compute depth for each question
    const depthOf: Record<string, number> = {};
    const resolving = new Set<string>();
    function getDepth(qid: string): number {
      if (depthOf[qid] !== undefined) return depthOf[qid];
      if (resolving.has(qid)) return 0;
      resolving.add(qid);
      if (parentOf[qid]) {
        depthOf[qid] = getDepth(parentOf[qid]) + 1;
      } else {
        depthOf[qid] = 0;
      }
      resolving.delete(qid);
      return depthOf[qid];
    }
    for (const q of qs) getDepth(q.id);

    // For each question, compute whether it's a child, last among siblings, etc.
    const isChildSet = new Set<string>(Object.keys(parentOf));
    const isLastChildMap: Record<string, boolean> = {};
    for (const [, kids] of Object.entries(childrenOf)) {
      kids.forEach((kid, i) => {
        isLastChildMap[kid] = i === kids.length - 1;
      });
    }
    const hasChildrenSet = new Set<string>(Object.keys(childrenOf));

    // For each question, compute connectorColumns:
    // an array of booleans, one per depth level from 1..depth,
    // indicating whether a vertical line should continue at that column.
    // A line continues if the ancestor at that depth still has more siblings below.
    const connectorColumns: Record<string, boolean[]> = {};
    for (const q of qs) {
      const d = depthOf[q.id];
      if (d === 0) {
        connectorColumns[q.id] = [];
        continue;
      }
      const cols: boolean[] = [];
      // Walk up the ancestry chain
      let currentId = q.id;
      for (let level = d; level >= 1; level--) {
        const pid = parentOf[currentId];
        // At this level, does the ancestor (currentId) have more siblings after it?
        const isLast = isLastChildMap[currentId] ?? true;
        cols.unshift(!isLast); // true = draw vertical line (more siblings below)
        currentId = pid;
      }
      connectorColumns[q.id] = cols;
    }

    // Collect all child IDs recursively for drag-disable purposes
    const allChildIds = new Set<string>();
    function collectAllChildren(qid: string) {
      const kids = childrenOf[qid] || [];
      for (const kid of kids) {
        allChildIds.add(kid);
        collectAllChildren(kid);
      }
    }
    for (const q of qs) collectAllChildren(q.id);

    return {
      allChildIds,
      isChildSet,
      isLastChildMap,
      hasChildrenSet,
      depthOf,
      connectorColumns,
      parentOf,
      childrenOf,
    };
  }, [questions, getConditionalChildIds]);

  const {
    allChildIds,
    isChildSet,
    isLastChildMap,
    hasChildrenSet,
    depthOf,
    connectorColumns: connectorColumnsMap,
    parentOf,
    childrenOf,
  } = connectorInfo;

  const sortableItems = useMemo(() => questions.map((q) => q.id), [questions]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const handleDragStart = (e: DragStartEvent) => setActiveId(e.active.id as string);
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const activeStr = active.id as string;
    const overStr = over.id as string;

    if (isChildSet.has(activeStr)) {
      const pid = parentOf[activeStr];
      const siblings = childrenOf[pid] || [];
      if (!siblings.includes(overStr)) return;
      moveChildGroup(scope, activeStr, overStr);
      return;
    }

    if (allChildIds.has(overStr)) return;
    moveQuestionGroup(scope, activeStr, overStr);
  };
  const activeQuestion = activeId ? questions.find((q) => q.id === activeId) : null;
  const activeDescendantIds = useMemo(() => {
    if (!activeId) return new Set<string>();
    const collect = (qid: string): string[] => {
      const kids = childrenOf[qid] || [];
      return [...kids, ...kids.flatMap(collect)];
    };
    return new Set(collect(activeId));
  }, [activeId, childrenOf]);
  const activeChildren = activeId
    ? (Array.from(activeDescendantIds)
        .map((cid) => questions.find((q) => q.id === cid))
        .filter(Boolean) as FormQuestion[])
    : [];

  const isInnermostList = (e: React.DragEvent) =>
    (e.target as HTMLElement).closest("[data-question-list]") === e.currentTarget;

  const handleExternalDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!isInnermostList(e)) return;
    try {
      const d = JSON.parse(e.dataTransfer.getData("application/json"));
      if (d.id) addQuestion(scope, d.id);
    } catch {
      console.error("Failed to add question", e);
    }
  };

  return (
    <div
      data-question-list
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setIsDragOver(isInnermostList(e));
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false);
      }}
      onDrop={handleExternalDrop}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          {questions.map((q, qi) => (
            <SortableQuestionItem
              key={q.id}
              question={q}
              scope={scope}
              index={qi}
              total={questions.length}
              isLastChild={isLastChildMap[q.id]}
              hasConditionalChildren={hasChildrenSet.has(q.id)}
              depth={depthOf[q.id] || 0}
              connectorColumns={connectorColumnsMap[q.id] || []}
              isDraggedDescendant={activeDescendantIds.has(q.id)}
              compact={compact}
            />
          ))}
        </SortableContext>
        <DragOverlay dropAnimation={{ duration: 200, easing: "ease" }}>
          {activeQuestion && (
            <QuestionDragOverlay
              question={activeQuestion}
              childQuestions={activeChildren}
              compact={compact}
            />
          )}
        </DragOverlay>
      </DndContext>

      {compact ? (
        <Stack gap={8} mt={4}>
          {questions.length === 0 && (
            <Text size="sm" c="dimmed">
              Add the fields each entry should ask
            </Text>
          )}
          <Group gap={6}>
            <AddFieldMenu scope={scope} compact />
          </Group>
          <Divider color="var(--border-default,#cfd4dd)" />
          <FieldDropZone
            scope={scope}
            isDragOver={isDragOver}
            label={emptyStateLabel}
            minHeight={64}
            background="var(--bg-neutral-default, #ffffff)"
          />
        </Stack>
      ) : (
        <FieldDropZone
          scope={scope}
          isDragOver={isDragOver}
          label={emptyStateLabel}
          minHeight={120}
        />
      )}
    </div>
  );
}

function FieldDropZone({
  scope,
  isDragOver,
  label,
  minHeight,
  background = "var(--bg-neutral-medium, #F0F2F5)",
}: {
  scope: QuestionScope;
  isDragOver: boolean;
  label?: string;
  minHeight: number;
  background?: string;
}) {
  const isNarrow = useIsNarrowEditor();
  const defaultLabel = isNarrow ? "Tap Add field above, or" : BUILDER_LABELS.emptyStateDesktop;
  return (
    <Paper
      p={minHeight > 80 ? "lg" : "md"}
      radius="var(--spacing-x-small,8px)"
      mt={4}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 6,
        border: `2px dashed ${isDragOver ? "#004ddb" : "#cfd4dd"}`,
        background: isDragOver ? "#e8f0fe" : background,
        color: isDragOver ? "#004ddb" : "#646d7e",
        fontSize: 13,
        transition: "all 0.15s",
        minHeight,
      }}
    >
      {isDragOver ? (
        <Text size="sm" c="inherit" fw={500}>
          Drop to add question
        </Text>
      ) : (
        <Flex gap={4} wrap="wrap" justify="center" align="baseline">
          <Text size="sm" c="inherit" fw={500}>
            {label || defaultLabel}
          </Text>
          <AddFieldMenu scope={scope} />
        </Flex>
      )}
    </Paper>
  );
}
